import React, { ReactNode } from 'react';

import PostCardsList from 'components/PostCardList';

import Breadcrumbs, { BreadcrumbsItemType } from 'ui/src/components/Breadcrumbs';
import Pagination from 'ui/src/components/Pagination';
import { POSTS_PER_PAGE } from 'utils/shared';
import Skeleton from 'ui/src/components/Skeleton';
import { LinkProps } from 'next/link';
import { PostCardListFragment } from 'gql/graphql-operations';
import PaginationLink from './PaginationLink';
import MainContainer from './MainContainer';

export type PostListProps = {
  title?: React.ReactNode;
  subtitle?: string;
  description?: string;
  customDescription?: ReactNode;
  posts: (PostCardListFragment | null)[];
  total: number;
  breadcrumbs: BreadcrumbsItemType[];
  withCategories?: boolean;
  currentPage?: number;
  isLoading?: boolean;
  withAuthorAvatar?: boolean;
  isLoadingTitle?: boolean;
  paginationHrefConstructor: (page: number) => LinkProps['href'];
};

function PostList({
  title,
  subtitle,
  description,
  posts,
  total,
  breadcrumbs,
  withCategories,
  customDescription,
  currentPage = 1,
  isLoading,
  withAuthorAvatar,
  isLoadingTitle,
  paginationHrefConstructor,
}: PostListProps): JSX.Element {
  return (
    <main>
      <MainContainer>
        <Breadcrumbs
          items={breadcrumbs}
          iconName="Home"
          iconClasses="bg-site-neutrals-700"
          customListClasses="w-full mt-[7.75rem] mb-11"
        />
        <div className="relative mb-11 w-full">
          <Skeleton isLoading={isLoadingTitle} classes="w-full h-11">
            <>
              {title && (
                <div className="display-l text-site-neutrals-500">
                  {subtitle ? (
                    <>
                      <span className="text-site-neutrals-700">{title}</span>{' '}
                      <h1 className="inline">{subtitle}</h1> ({total})
                    </>
                  ) : (
                    <h1 className="text-site-neutrals-700">{title}</h1>
                  )}
                </div>
              )}
              {description && <p className="paragraph-m mt-3 pb-4 text-gray-400">{description}</p>}
              {customDescription}
            </>
          </Skeleton>
        </div>
        {/* Temporally commented because we don't have the functionality implemented. The props come from useSortPostsRouter */}
        {/* <div className="flex justify-end mx-4 mb-7 sm:mx-auto">
          <Select
            name="sort"
            options={sortOptions}
            value={sortValue}
            onChange={onChange}
            onBlur={onBlur}
            classes="w-[22.5rem]"
            label=""
            labelClasses="hidden"
          />
        </div> */}
      </MainContainer>
      <section className="bg-site-neutrals-25 relative w-full pb-20">
        <div className="bg-stripeTop absolute left-0 top-0 h-56 w-full" />
        <MainContainer>
          <PostCardsList
            classes="relative"
            isLoading={isLoading}
            posts={posts}
            withCategories={withCategories}
            withAuthorAvatar={withAuthorAvatar}
            withSummary
            CustomHeadingTag="h2"
          />
          <Pagination
            total={total}
            currentPage={currentPage}
            itemsPerPage={POSTS_PER_PAGE}
            classes="mt-7 lgL:mt-20"
            hrefConstructor={paginationHrefConstructor}
            LinkComponent={PaginationLink}
          />
        </MainContainer>
      </section>
    </main>
  );
}

export default PostList;
